import { createApi } from "@reduxjs/toolkit/query/react";
import { stringify } from "qs";

import { API_ENDPOINT } from "../../../config/api";
import { baseQuery } from "../../../shared/api/base-query";
import { fetchWithAuth } from "../../../shared/api/fetch-with-auth";
import { downloadResponseFile } from "../../../shared/utils/download-response-file";
import { isRequestAborted } from "../../../shared/utils/is-request-aborted";
import { TechOperationAssetEntityType } from "../../tech-operations/shared/types/tech-operation-asset-entity";
import { AssetStockBalanceDto } from "../shared/dtos/asset-stock-balance";
import { AssetStockBalancesResponseDto } from "../shared/dtos/asset-stock-balances-response";

const DEFAULT_FILENAME = "Остатки_ТМЦ.xlsx";

const LARGE_PAGE_SIZE = 10000;

type FetchAssetStockBalancesArgs = {
  farmId: string;
  assetCategory?: TechOperationAssetEntityType[];
  name?: string[];
  measureUnit?: string[];
};

export type AssetStockBalancesResult = {
  items: AssetStockBalanceDto[];
  lastSyncAt: string;
  totalCount: number;
};

export const assetStockBalancesApiSlice = createApi({
  reducerPath: "api-asset-stocks",
  baseQuery,
  tagTypes: ["AssetStockBalances"],
  endpoints: (builder) => ({
    fetchAssetStockBalances: builder.query<
      AssetStockBalancesResult,
      FetchAssetStockBalancesArgs
    >({
      query: ({ farmId, assetCategory, name, measureUnit }) => {
        const params = stringify(
          {
            farmId: [farmId],
            ...(assetCategory?.length ? { assetCategory } : {}),
            ...(name?.length ? { name } : {}),
            ...(measureUnit?.length ? { measureUnit } : {}),
            pageSize: LARGE_PAGE_SIZE,
          },
          { arrayFormat: "repeat", encodeValuesOnly: true }
        );
        return { url: `AssetStockBalances?${params}`, method: "GET" };
      },
      providesTags: ["AssetStockBalances"],
      transformResponse: (
        response: AssetStockBalancesResponseDto
      ): AssetStockBalancesResult => ({
        items: response.data,
        lastSyncAt: response.meta.lastSyncAt,
        totalCount: response.meta.totalCount,
      }),
    }),

    exportAssetStockBalances: builder.mutation<
      void,
      {
        farmId: string;
        assetCategory?: string[];
        name?: string[];
        measureUnit?: string[];
      }
    >({
      queryFn: async (args, { signal }) => {
        try {
          const url = `${API_ENDPOINT}/AssetStockBalances/export`;
          const searchParams = stringify(
            {
              farmId: [args.farmId],
              ...(args.assetCategory?.length
                ? { assetCategory: args.assetCategory }
                : {}),
              ...(args.name?.length ? { name: args.name } : {}),
              ...(args.measureUnit?.length ? { measureUnit: args.measureUnit } : {}),
            },
            { arrayFormat: "repeat" }
          );
          const response = await fetchWithAuth(`${url}?${searchParams}`, { signal });
          if (!response.ok) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: `${response.status} ${response.statusText}`,
              },
            };
          }
          await downloadResponseFile(response, DEFAULT_FILENAME);
          return { data: undefined };
        } catch (error) {
          if (isRequestAborted(error)) {
            return {
              error: { status: "CUSTOM_ERROR", error: "AbortError" },
            };
          }
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "Ошибка при формировании отчёта",
            },
          };
        }
      },
    }),
  }),
});

export const { useFetchAssetStockBalancesQuery } = assetStockBalancesApiSlice;
